import { useEffect, useMemo, useRef, useState } from 'react'
import { useBack } from '@/hooks/useBack'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Loader2, X, ChevronLeft, Volume2, VolumeX, AlertCircle, Keyboard } from 'lucide-react'
import { useQuestions } from '@/hooks/useQuestions'
import { useAuth } from '@/hooks/useAuth'
import { useQuizStore } from '@/stores/quizStore'
import { useConceptMastery } from '@/hooks/useConceptMastery'
import { QuestionCard } from '@/components/QuestionCard'
import type { SelfGrade } from '@/components/QuestionCard'
import { ProgressBar } from '@/components/ProgressBar'
import { QuitQuizDialog } from '@/components/QuitQuizDialog'
import { IncompletePartsDialog } from '@/components/IncompletePartsDialog'
import { GradePartsDialog } from '@/components/GradePartsDialog'
import { SelfGradeScreen } from '@/components/SelfGradeScreen'
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp'
import { QuestionInfoButton } from '@/components/QuestionInfoButton'
import { PreQuizConcepts } from '@/components/PreQuizConcepts'
import { ConceptPopup } from '@/components/wiki/ConceptPopup'
import { useConceptPopup } from '@/hooks/useConceptPopup'
import { useConceptHighlight } from '@/hooks/useConceptHighlight'
import { mentionWalk } from '@/lib/conceptMentions'
import { loadConceptLinks, parseConceptLinksParam } from '@/lib/quizConceptLinks'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PRACTICE_EXAM_LABEL } from '@/lib/pastExams'
import { isAnswerCorrect, isMultiPartAnswerComplete } from '@/lib/parser'
import { ungradedWrittenParts, writtenParts } from '@/lib/writtenAnswer'
import { pendingAnswerFor, tagPendingAnswer } from '@/lib/pendingAnswer'
import { resumesQuiz } from '@/lib/quizResume'
import { loadRevealMode, parseRevealMode } from '@/lib/revealMode'
import { difficultyFromParam, difficultyLevelsFromParam } from '@/lib/quizDifficulty'
import { timeAllowanceSeconds } from '@/lib/quizTiming'
import { QuizTimer } from '@/components/QuizTimer'
import { RaidHitChip } from '@/components/actuaria/RaidHitChip'
import { reportRaidHit } from '@/lib/actuaria/raidClient'
import { ACTUARIA_ENABLED } from '@/lib/featureFlags'
import { moveScreen, REPLACE_AS_PUSH } from '@/lib/viewTransition'
import type { QuestionFilter, Difficulty, QuizMode } from '@/lib/parser'
import { decayIfStale } from '@/lib/mastery'
import { useTodayPlanConcepts } from '@/hooks/useTodayPlanConcepts'
import { TOPIC_TO_EXAM_ID } from '@/hooks/useExamProgress'
import type { ConceptMasteryRecord } from '@/lib/mastery'
import { slugForLink } from '@/lib/conceptMatch'
import { useSoundEffects } from '@/hooks/useSoundEffects'
import { usePageKeyboard } from '@/hooks/useKeyboard'
import { trackQuizStarted, trackQuestionAnswered, trackQuizCompleted, trackFirstQuiz, trackFirstCorrect, trackReminderSession } from '@/lib/analytics'

export default function Quiz() {
  const [searchParams] = useSearchParams()
  const { search } = useLocation()
  const navigate = useNavigate()
  // Out of the quiz the way the reader came in — the exam's builder, a study
  // guide, the search they were on, Concept Craft — back where it was left.
  // `from` only says where to go for a quiz opened straight from its address.
  const quizFrom = searchParams.get('from')
  const back = useBack({
    to: quizFrom === 'search' ? '/search' : quizFrom === 'dashboard' ? '/dashboard' : '/',
    label: quizFrom === 'search' ? 'Search' : quizFrom === 'dashboard' ? 'Dashboard' : 'Quiz',
  })
  const { user } = useAuth()
  const { records: masteryRecords, loading: masteryLoading } = useConceptMastery()

  const mode = (searchParams.get('mode') as QuizMode | null) ?? 'quiz'
  // reveal='during' shows the explanation after each answer; 'end' defers the
  // whole lot to /review. The launching surface says so in the URL; a surface
  // that doesn't (a quiz started from Search, say) falls back to the reader's
  // saved choice rather than to a fixed 'during'.
  const reveal = parseRevealMode(searchParams.get('reveal')) ?? loadRevealMode(mode)
  const countParam = searchParams.get('count')
  // Timed: a countdown sized to what these questions would get on the real
  // paper (lib/quizTiming.ts). Set by the quiz builder's settings menu.
  const timed = searchParams.get('timed') === '1'
  // Whether the concepts a question names are underlined to tap and read
  // (lib/quizConceptLinks.ts) — the builder says so in the URL, anything else
  // falls back to the reader's saved choice for this mode.
  const linkConcepts = parseConceptLinksParam(searchParams.get('links')) ?? loadConceptLinks(mode)
  // A raid run (Actuaria, docs/actuaria-online.md §7.7): an ordinary quiz whose
  // first answer to each question is also handed to the cohort's boss, which
  // the server marks — `raid` is the draw quiz/api/raid.js served it as.
  const raidDraw = ACTUARIA_ENABLED ? searchParams.get('raid') : null

  const filters: QuestionFilter = useMemo(() => {
    const topicsParam = searchParams.get('topics')
    const idsParam = searchParams.get('ids')
    const selection = searchParams.get('selection')

    let ids: string[] | undefined
    if (idsParam) {
      ids = idsParam.split(',').filter(Boolean)
    } else if (selection === 'stored') {
      try {
        const raw = sessionStorage.getItem('actuarial_selected_ids')
        if (raw) ids = JSON.parse(raw) as string[]
      } catch { /* ignore */ }
    }

    const conceptsParam = searchParams.get('concepts')
    const yearParam = searchParams.get('year')
    const sessionParam = searchParams.get('session')
    return {
      exam: searchParams.get('exam') ?? undefined,
      topics: topicsParam ? topicsParam.split(',') : undefined,
      concepts: conceptsParam ? conceptsParam.split(',') : undefined,
      difficulty: (searchParams.get('difficulty') as Difficulty | null) ?? undefined,
      difficulties: difficultyLevelsFromParam(searchParams.get('levels')) ?? undefined,
      difficultyTarget: difficultyFromParam(searchParams.get('level')) ?? undefined,
      mode,
      count: countParam ? Number(countParam) : undefined,
      ids,
      concept: searchParams.get('concept') ?? undefined,
      year: yearParam ? Number(yearParam) : undefined,
      session: sessionParam ?? undefined,
    }
  }, [searchParams])  // eslint-disable-line react-hooks/exhaustive-deps

  const { questions, loading, error } = useQuestions(filters)

  const {
    questions: storeQuestions,
    currentIndex,
    responses,
    flaggedIds,
    status,
    manualGrades,
    startQuiz,
    answerQuestion,
    clearAnswer,
    nextQuestion: advanceQuestion,
    goToPreviousQuestion: stepBackQuestion,
    goToQuestion,
    toggleFlag,
    setManualGrade,
    completeQuiz,
    resetQuiz,
    leaveQuiz,
    timer,
    startTimer,
    pending,
    setPending,
    conceptListDismissed,
    dismissConceptList,
  } = useQuizStore()

  // A quiz outlives this page: the reader can go anywhere in the app mid-quiz
  // and the "Return to quiz" pill (components/QuizResumeButton.tsx) brings them
  // back here. Coming back at the URL the session was started under — by the
  // pill, or by the browser's Back — picks it up where it was left; any other
  // URL is a new quiz (lib/quizResume.ts). Decided once, on arrival, from the
  // store as it stood then.
  const [resumed] = useState(() => resumesQuiz(useQuizStore.getState(), search))
  // Set on Quit. The page change it asks for lands a frame or two later (it is
  // a view transition — components/PaperRouter.tsx), and in between this page
  // is still mounted over a store that has just gone back to idle: without the
  // flag it would draw a fresh quiz from the questions it has loaded, and the
  // reader would arrive wherever they were going to a "Return to quiz" pill
  // for a quiz they never started.
  const leavingRef = useRef(false)

  // Next and Back turn the question like a sheet on a pile — the answered one
  // is flicked off and the next lifts into place — rather than swapping it in
  // place (see "Paper on a desk" in index.css). Past the last question Next
  // finishes the quiz, which isn't a sheet to turn. A jump from the progress
  // bar stays a jump: it can be dragged through a dozen questions a second.
  function nextQuestion() {
    if (currentIndex + 1 >= storeQuestions.length) {
      advanceQuestion()
      return
    }
    moveScreen('turn', advanceQuestion)
  }

  function goToPreviousQuestion() {
    if (currentIndex <= 0) return
    moveScreen('return', stepBackQuestion)
  }

  // Reset store on every new quiz navigation so filters always take effect —
  // unless this is the session in progress being picked back up.
  useEffect(() => {
    if (!resumed) resetQuiz()
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // Start the quiz once questions load — guard prevents re-triggering on re-renders
  useEffect(() => {
    if (!loading && questions.length > 0 && status === 'idle' && !leavingRef.current) {
      startQuiz(questions, mode, search)
      const exam = questions[0]?.exam ?? 'Unknown'
      trackQuizStarted({ mode, exam, question_count: questions.length })
      trackFirstQuiz({ mode, exam })
    }
  }, [loading, questions, status, mode, search, startQuiz])

  const { enabled: soundEnabled, toggle: toggleSound, play: playSound, resetCombo: resetSoundCombo } = useSoundEffects()


  const [showQuitDialog, setShowQuitDialog] = useState(false)
  const [showIncompletePartsDialog, setShowIncompletePartsDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // The pre-confirmation selection (`pending`, from the store) is not an
  // answer until "Confirm Answer". It is tagged with the question it was
  // entered for so it is never read back on another one (see
  // lib/pendingAnswer.ts), and kept in the store so a half-typed multi-part
  // answer is still there when the reader comes back from a lookup.

  // Self-grading screen for reveal='end' (mock exam) with written questions
  const [showSelfGradeScreen, setShowSelfGradeScreen] = useState(false)
  // keyed by `${questionId}__${partLabel}`
  const [essaySelfGrades, setEssaySelfGrades] = useState<Record<string, SelfGrade>>({})
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  // Where the current question came from — its sitting and its vault file.
  const [showQuestionInfo, setShowQuestionInfo] = useState(false)

  const essayQuestions = useMemo(
    () => storeQuestions.filter(q => writtenParts(q).length > 0),
    [storeQuestions],
  )
  // Leaving a question whose written parts aren't all graded asks for the
  // grades first (components/GradePartsDialog.tsx). The labels are the parts
  // that were ungraded when Next was pressed — kept fixed while the dialog is
  // up, so a part doesn't vanish from it the moment it is graded.
  const [gradePrompt, setGradePrompt] = useState<string[] | null>(null)

  // Tracks whether the user clicked "Change Answer" on the current question
  const [isChangingAnswer, setIsChangingAnswer] = useState(false)

  // ── Pre-quiz concept list ────────────────────────────────────────────────
  // The concepts this quiz introduces — those still at New — listed before the
  // first question, each one openable in the concept popup so the reader can
  // look it over first. A right answer on one levels it to Level 1 and collects
  // its card (docs/flashcard-collection.md). Only applies to ordinary quizzes
  // (never mock exams). Whether it has been read past is the store's
  // `conceptListDismissed`, so a resumed quiz doesn't show it twice.

  const newQuizConcepts = useMemo(() => {
    if (mode !== 'quiz') return []
    const lookup = new Map<string, ConceptMasteryRecord>()
    for (const r of masteryRecords) lookup.set(r.concept_slug.toLowerCase(), r)
    const now = new Date()
    const seen = new Set<string>()
    const result: string[] = []
    for (const q of storeQuestions) {
      for (const link of q.wiki_link) {
        const slug = slugForLink(link)
        if (!slug) continue
        const key = slug.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        const rec = lookup.get(key)
        const state = rec ? decayIfStale(rec, now).state : 'new'
        if (state === 'new') result.push(slug)
      }
    }
    return result
  }, [storeQuestions, masteryRecords, mode])

  // Today's study plan for the exam this quiz is drawn from, so the list can
  // mark the concepts that would move the plan forward. The quiz's
  // filters may not name the exam (an ids/concepts launch doesn't), so read it
  // off the questions themselves.
  const quizExamKey = useMemo(() => {
    const label = storeQuestions[0]?.exam ?? filters.exam
    return label ? (TOPIC_TO_EXAM_ID[label] ?? null) : null
  }, [storeQuestions, filters.exam])
  const planConcepts = useTodayPlanConcepts(quizExamKey)

  // Show the list only at the very start of the session (before any answer) so
  // it never interrupts a quiz already in progress, and only once mastery has
  // loaded so the New classification is accurate.
  const showConceptList =
    mode === 'quiz' &&
    !conceptListDismissed &&
    !masteryLoading &&
    status === 'active' &&
    Object.keys(responses).length === 0 &&
    newQuizConcepts.length > 0

  // Whether the list decision is still pending: at the very start of a quiz
  // session, `showConceptList` depends on mastery, so if mastery is still
  // loading we can't yet tell whether the list should appear. Hold the loading
  // state (below) rather than render the first question and then swap it out
  // for the list — that swap is a jarring "flash the first question".
  const awaitingConceptListDecision =
    mode === 'quiz' &&
    !conceptListDismissed &&
    masteryLoading &&
    status === 'active' &&
    Object.keys(responses).length === 0

  // ── Timed ────────────────────────────────────────────────────────────────
  // The budget is the set's, fixed once the questions are in; the clock starts
  // on the first question, not under the pre-quiz concept list — reading that
  // list is preparation, not the paper. It lives in the store and keeps
  // running while the reader is elsewhere in the app: time spent looking
  // something up is time spent on the paper.
  const timeAllowance = useMemo(
    () => (timed ? timeAllowanceSeconds(storeQuestions) : null),
    [timed, storeQuestions],
  )
  const questionOnScreen =
    status !== 'idle' && status !== 'complete' && !showConceptList && !awaitingConceptListDecision
  useEffect(() => {
    if (timeAllowance !== null && questionOnScreen && timer === null) startTimer(timeAllowance)
  }, [timeAllowance, questionOnScreen, timer, startTimer])

  // Scroll to top whenever the question changes. The pending selection needs no
  // clearing here — it is tagged with its question and read back through
  // `pendingAnswer` below, so it simply stops applying.
  useEffect(() => {
    setIsChangingAnswer(false)
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [currentIndex])

  const currentQuestion = storeQuestions[currentIndex]

  // ── Concepts named in the question ───────────────────────────────────────
  // A concept the question writes out is underlined (lib/conceptMentions.ts);
  // a tap opens it in the popup, the split pane the study guide reads one in,
  // and the popup walks every mention on the card the way the study guide walks
  // its links — the one it shows highlighted and scrolled to, the rest faded
  // (hooks/useConceptHighlight.ts). Only the question's own tags are looked
  // for, and only words already on screen are marked.
  const cardRef = useRef<HTMLDivElement>(null)
  const openConceptAt = useConceptPopup(s => s.openAt)
  const conceptPopupOpen = useConceptPopup(s => s.open)
  const conceptSource = currentQuestion ? `quiz:${currentQuestion.id}` : undefined
  useConceptHighlight(cardRef, conceptSource)
  const conceptNames = useMemo(() => {
    if (!linkConcepts || !currentQuestion) return undefined
    const names = currentQuestion.wiki_link.map(slugForLink).filter((n): n is string => !!n)
    return names.length > 0 ? [...new Set(names)] : undefined
  }, [linkConcepts, currentQuestion])

  // A popup opened on one question is about that question: turning the page,
  // or leaving the quiz, closes it.
  useEffect(() => {
    const { open, sourcePath, close } = useConceptPopup.getState()
    if (open && sourcePath?.startsWith('quiz:')) close()
  }, [currentIndex])
  useEffect(() => () => {
    const { open, sourcePath, close } = useConceptPopup.getState()
    if (open && sourcePath?.startsWith('quiz:')) close()
  }, [])

  function handleMentionClick(e: React.MouseEvent) {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-concept-mention]')
    const root = cardRef.current
    if (!target || !root || !root.contains(target) || !conceptSource) return
    e.preventDefault()
    const mentions = Array.from(root.querySelectorAll<HTMLElement>('[data-concept-mention]'))
    const { list, occurrences } = mentionWalk(mentions.map(el => el.dataset.conceptMention ?? ''))
    const name = (target.dataset.conceptMention ?? '').toLowerCase()
    openConceptAt(
      list,
      Math.max(0, list.findIndex(r => r.name.toLowerCase() === name)),
      conceptSource,
      null,
      null,
      { occurrences, occurrenceIndex: Math.max(0, mentions.indexOf(target)), walk: 'question' },
    )
  }

  // The Info panel's concept rows open the same popup, over every concept the
  // question is tagged with — whether the text names it or not.
  function openTaggedConcept(links: string[], index: number) {
    if (!conceptSource) return
    const refs = links.map(link => ({ kind: 'concept' as const, name: slugForLink(link) ?? link }))
    openConceptAt(refs, index, conceptSource, null, null, { walk: 'question' })
  }

  const pendingAnswer = pendingAnswerFor(pending, currentQuestion)
  const committedAnswer = currentQuestion ? (responses[currentQuestion.id]?.chosen ?? null) : null
  const isLocked = status === 'reviewing'
  // What to visually highlight: committed answer (locked) or pending selection
  const displayAnswer = isLocked ? committedAnswer : pendingAnswer
  const isLastQuestion = currentIndex + 1 >= storeQuestions.length
  // Multi-part questions show the Confirm button immediately (no need to wait
  // for a pending selection) — incomplete submissions are caught with a confirm dialog
  const showConfirmButton = !isLocked && (pendingAnswer !== null || currentQuestion?.type === 'multi-part')

  const anyDialogOpen = showQuitDialog || showIncompletePartsDialog || showSelfGradeScreen || showShortcutsHelp || showQuestionInfo || gradePrompt !== null

  // Per-part grades for a multi-part question, read back from the store keyed
  // by part label (the store keys them as `${id}__${label}`).
  function partGradesFor(questionId: string): Record<string, SelfGrade> {
    return Object.fromEntries(
      Object.entries(manualGrades)
        .filter(([k]) => k.startsWith(`${questionId}__`))
        .map(([k, v]) => [k.slice(questionId.length + 2), v]),
    )
  }

  // Select an MC answer by its 0-based index in the options array
  function selectAnswerByIndex(i: number) {
    if (!currentQuestion || isLocked) return
    if (currentQuestion.type === 'multi-part' || !currentQuestion.options?.length) return
    const option = currentQuestion.options[i]
    if (option) {
      // Picking with the number keys should sound like picking with the mouse;
      // the delegated listener only sees pointer/Enter activations.
      playSound('select')
      handleSelectAnswer(option.key)
    }
  }

  usePageKeyboard({
    '1': () => selectAnswerByIndex(0),
    '2': () => selectAnswerByIndex(1),
    '3': () => selectAnswerByIndex(2),
    '4': () => selectAnswerByIndex(3),
    'Enter': () => {
      if (isLocked) handleNextFromAnswer()
      else if (pendingAnswer !== null) handleConfirmAnswer()
    },
    'ArrowRight': () => { if (isLocked) handleNextFromAnswer() },
    'ArrowLeft': () => { if (currentIndex > 0) goToPreviousQuestion() },
    'f': () => { if (currentQuestion) toggleFlag(currentQuestion.id) },
    // The confirmation is only audible when the toggle just turned sound *on*,
    // which is exactly when the user needs to hear that it worked.
    'm': () => { toggleSound(); playSound('toggleOn') },
    '?': () => setShowShortcutsHelp(v => !v),
    'i': () => { if (currentQuestion) setShowQuestionInfo(v => !v) },
  }, !anyDialogOpen && !conceptPopupOpen && !showConceptList && status !== 'idle' && status !== 'complete')

  function handleQuit() {
    leavingRef.current = true
    leaveQuiz()
    back.go()
  }

  function handleSelectAnswer(key: string) {
    if (key === pendingAnswer) {
      // Second tap on already-selected answer confirms it
      handleConfirmAnswer()
    } else {
      setPending(tagPendingAnswer(currentQuestion, key))
    }
  }

  // Next (or Finish) on an answered question — the button, Enter, →, or a tap
  // on a locked answer. With the answers shown as the quiz goes, written parts
  // still waiting for a self-grade are asked about first.
  function handleNextFromAnswer() {
    if (showExplanation && currentQuestion) {
      const ungraded = ungradedWrittenParts(currentQuestion, partGradesFor(currentQuestion.id))
      if (ungraded.length > 0) {
        setGradePrompt(ungraded.map(p => p.label))
        return
      }
    }
    moveOn()
  }

  function moveOn() {
    if (isLastQuestion) {
      handleFinish()
    } else {
      nextQuestion()
    }
  }

  function handleConfirmAnswer() {
    if (!currentQuestion) return

    if (currentQuestion.type === 'multi-part') {
      const answer = pendingAnswer ?? '{}'
      if (!isMultiPartAnswerComplete(currentQuestion, answer)) {
        setShowIncompletePartsDialog(true)
        return
      }
      commitAnswer(answer)
      return
    }

    if (pendingAnswer) commitAnswer(pendingAnswer)
  }

  function commitAnswer(answer: string) {
    if (!currentQuestion) return
    const correct = isAnswerCorrect(currentQuestion, answer)
    // Right answers get the arpeggio; wrong ones stay silent on purpose — the
    // reveal already says it, and a buzzer is punishment, not feedback. What a
    // miss does instead is end the run: the arpeggio climbs a step for each
    // right answer in a row, and this drops it back to where it started.
    // With the answers held back for the review screen the arpeggio would give
    // the verdict away, so it only plays when the answer is revealed anyway.
    if (correct && !isChangingAnswer && reveal === 'during') {
      playSound('correct')
    } else if (!correct) {
      resetSoundCombo('correct')
    }
    answerQuestion(currentQuestion.id, answer)
    if (ACTUARIA_ENABLED && raidDraw && !isChangingAnswer) void reportRaidHit(raidDraw, currentQuestion.id, answer)
    setIsChangingAnswer(false)
    trackQuestionAnswered({ question_id: currentQuestion.id, is_correct: correct, exam: currentQuestion.exam, mode })
    if (correct) trackFirstCorrect({ mode, exam: currentQuestion.exam })
  }

  function handleChangeAnswer() {
    if (!currentQuestion) return
    const previous = committedAnswer
    clearAnswer(currentQuestion.id)
    setPending(tagPendingAnswer(currentQuestion, previous))
    setIsChangingAnswer(true)
  }

  // Show the explanation inline whenever the reader asked to see answers as they
  // go. That is a choice, not a property of the mode: a practice exam run for
  // feedback reveals too, and a quiz run as a dry run holds everything back.
  const showExplanation = isLocked && reveal === 'during'

  async function submitQuiz() {
    if (isSubmitting) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const correctCount = storeQuestions.filter(q => {
        const r = responses[q.id]
        return r && isAnswerCorrect(q, r.chosen)
      }).length
      trackQuizCompleted({ mode, exam: storeQuestions[0]?.exam ?? 'Unknown', question_count: storeQuestions.length, correct_count: correctCount })
      trackReminderSession({ exam: storeQuestions[0]?.exam ?? 'Unknown', question_count: storeQuestions.length, correct_count: correctCount })
      playSound('complete')
      // Persist essay self-grades collected on the reveal='end' self-grade screen
      // into the store (keyed `${id}__${label}`) so they're saved with the session
      // and shown on the /review end screen — otherwise they'd be lost.
      for (const [key, grade] of Object.entries(essaySelfGrades)) {
        setManualGrade(key, grade)
      }
      await completeQuiz(user?.id ?? null, masteryRecords)
      // In the quiz's place, not after it: Back from the review goes to where
      // the quiz was started, never onto a quiz that is over.
      navigate(`/review?${searchParams.toString()}`, { replace: true, state: REPLACE_AS_PUSH })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save quiz. Please try again.')
      setIsSubmitting(false)
    }
  }

  function handleFinish() {
    if (reveal === 'end' && essayQuestions.length > 0) {
      setShowSelfGradeScreen(true)
    } else {
      submitQuiz()
    }
  }

  // ── Loading state ────────────────────────────────────────────────────
  // A resumed session already has its questions; the fresh draw `useQuestions`
  // makes underneath it is never used, so neither its loading nor its errors
  // hold the quiz up.
  if ((!resumed && (loading || (status === 'idle' && !error))) || awaitingConceptListDecision) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading questions…</p>
      </div>
    )
  }

  // ── Error state ──────────────────────────────────────────────────────
  if (error && !resumed) {
    return (
      <div className="container max-w-md mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Failed to load questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Empty state ──────────────────────────────────────────────────────
  if (!resumed && !loading && questions.length === 0) {
    return (
      <div className="container max-w-md mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>No questions found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              No questions match your selected filters. Try different topics or difficulty.
            </p>
            <Button onClick={back.go} variant="outline" className="w-full">
              Back to {back.label}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!currentQuestion) return null

  // ── Pre-quiz concept list ────────────────────────────────────────────────
  if (showConceptList) {
    return (
      <PreQuizConcepts
        concepts={newQuizConcepts}
        planConcepts={planConcepts}
        onStart={dismissConceptList}
        onQuit={handleQuit}
      />
    )
  }

  const isFlagged = flaggedIds.includes(currentQuestion.id)

  // ── Self-grading screen (reveal='end' with written questions) ────────────
  if (showSelfGradeScreen) {
    return (
      <SelfGradeScreen
        questions={essayQuestions}
        numbers={Object.fromEntries(storeQuestions.map((q, i) => [q.id, i + 1]))}
        responses={responses}
        grades={essaySelfGrades}
        onGrade={(questionId, partLabel, grade) =>
          setEssaySelfGrades(prev => ({ ...prev, [`${questionId}__${partLabel}`]: grade }))
        }
        onBack={() => setShowSelfGradeScreen(false)}
        onSubmit={submitQuiz}
        submitLabel={mode === 'mock-exam' ? 'Submit Exam' : 'Finish Quiz'}
        isSubmitting={isSubmitting}
        submitError={submitError}
      />
    )
  }

  // Per-part self-grades for the current multi-part question.
  const partGradeMap = partGradesFor(currentQuestion.id)
  const setPartGrade = (partLabel: string, grade: SelfGrade) =>
    setManualGrade(`${currentQuestion.id}__${partLabel}`, grade)

  return (
    <>
    <div
      className="container max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5"
      // Below lg the concept popup docks as a bottom sheet; keep the question
      // and its buttons scrollable out from under it, as the study guide does.
      style={conceptPopupOpen ? { paddingBottom: 'calc(var(--concept-split-height, 50vh) + 1.5rem)' } : undefined}
    >
      {ACTUARIA_ENABLED && raidDraw && <RaidHitChip />}
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          data-tour="quit-quiz"
          onClick={() => setShowQuitDialog(true)}
          className="-ml-3 h-11 px-3 text-base text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5 mr-1.5" />
          Quit {mode === 'mock-exam' ? 'exam' : 'quiz'}
        </Button>

        <div className="flex items-center gap-2">
          <span
            className={
              'inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ' +
              (mode === 'mock-exam'
                ? 'bg-primary/10 text-primary border-primary/30'
                : 'bg-muted text-muted-foreground border-border')
            }
          >
            {mode === 'mock-exam' ? PRACTICE_EXAM_LABEL : 'Quiz'}
          </span>
          {timer !== null && (
            <QuizTimer startedAt={timer.startedAt} allowanceSeconds={timer.allowanceSeconds} />
          )}
          <Button
            variant="ghost"
            size="icon"
            data-sound="none"
            onClick={() => { toggleSound(); playSound('toggleOn') }}
            aria-label={soundEnabled ? 'Mute sounds' : 'Unmute sounds'}
            className="h-11 w-11 -mr-2 text-muted-foreground hover:text-foreground sm:mr-0"
          >
            {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowShortcutsHelp(true)}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
            className="h-11 w-11 text-muted-foreground hover:text-foreground hidden sm:flex"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="pb-2">
        <ProgressBar
          current={currentIndex + 1}
          total={storeQuestions.length}
          onNavigate={goToQuestion}
          flaggedIds={flaggedIds}
          questionIds={storeQuestions.map(q => q.id)}
          isFlagged={isFlagged}
          onFlag={() => toggleFlag(currentQuestion.id)}
          info={
            <QuestionInfoButton
              question={currentQuestion}
              open={showQuestionInfo}
              onOpenChange={setShowQuestionInfo}
              // Topic and difficulty are a hint while the question is live, so
              // they only join the panel once the answer is committed.
              showStudyMeta={isLocked}
              onOpenConcept={openTaggedConcept}
            />
          }
        />
      </div>

      {showQuitDialog && (
        <QuitQuizDialog
          mode={mode}
          onCancel={() => setShowQuitDialog(false)}
          onConfirm={handleQuit}
          onFinish={Object.keys(responses).length > 0 ? submitQuiz : undefined}
        />
      )}

      {showIncompletePartsDialog && (
        <IncompletePartsDialog
          onCancel={() => setShowIncompletePartsDialog(false)}
          onConfirm={() => {
            setShowIncompletePartsDialog(false)
            commitAnswer(pendingAnswer ?? '{}')
          }}
        />
      )}

      {gradePrompt && (
        <GradePartsDialog
          parts={(currentQuestion.parts ?? []).filter(p => gradePrompt.includes(p.label))}
          grades={partGradeMap}
          onGrade={setPartGrade}
          isOnlyPart={(currentQuestion.parts ?? []).length === 1}
          continueLabel={isLastQuestion ? `Finish ${mode === 'mock-exam' ? 'Exam' : 'Quiz'}` : 'Next Question'}
          onContinue={() => {
            setGradePrompt(null)
            moveOn()
          }}
          onCancel={() => setGradePrompt(null)}
        />
      )}

      {showShortcutsHelp && (
        <KeyboardShortcutsHelp
          context="quiz"
          onClose={() => setShowShortcutsHelp(false)}
        />
      )}

      {/* The sheet Next and Back turn. A tap on a concept it names opens that
          concept (handleMentionClick); everything else passes through. */}
      <div ref={cardRef} className="paper-sheet mt-4" onClick={handleMentionClick}>
        <QuestionCard
          // Keyed by question so nothing the card holds locally — a typed
          // free-entry answer, a self-grade — can outlive the question it
          // was entered for.
          key={currentQuestion.id}
          question={currentQuestion}
          selectedAnswer={displayAnswer}
          onAnswer={handleSelectAnswer}
          showExplanation={showExplanation}
          isLocked={isLocked}
          onNext={isLocked ? handleNextFromAnswer : undefined}
          selfGrade={currentQuestion.type === 'free-entry' ? manualGrades[currentQuestion.id] : undefined}
          onSelfGrade={currentQuestion.type === 'free-entry' ? (grade) => setManualGrade(currentQuestion.id, grade) : undefined}
          // Multi-part grades (both graded free-entry parts and answerless essay
          // parts) are persisted to the store under `${id}__${label}` so they
          // survive navigation and show identically on the /review end screen.
          // Essay parts route through essaySelfGrades in the card, so wire both.
          partManualGrades={currentQuestion.type === 'multi-part' ? partGradeMap : undefined}
          onPartManualGrade={currentQuestion.type === 'multi-part' ? setPartGrade : undefined}
          essaySelfGrades={currentQuestion.type === 'multi-part' ? partGradeMap : undefined}
          onEssaySelfGrade={currentQuestion.type === 'multi-part' ? setPartGrade : undefined}
          conceptNames={conceptNames}
        />
      </div>

      {submitError && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {submitError}
        </div>
      )}

      {(currentIndex > 0 || status === 'reviewing' || showConfirmButton) && (
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div>
            {currentIndex > 0 && (
              <Button variant="outline" size="lg" onClick={goToPreviousQuestion}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {showConfirmButton && (
              <Button
                onClick={handleConfirmAnswer}
                size="lg"
                data-tour="confirm-answer"
                className="bg-foreground text-background hover:bg-foreground/90"
              >
                Confirm Answer
              </Button>
            )}

            {isLocked && (
              <>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleChangeAnswer}
                >
                  Change Answer
                </Button>
                {isLastQuestion ? (
                  <Button
                    onClick={handleNextFromAnswer}
                    size="lg"
                    disabled={isSubmitting}
                    className="bg-foreground text-background hover:bg-foreground/90"
                  >
                    {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Finish {mode === 'mock-exam' ? 'Exam' : 'Quiz'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNextFromAnswer}
                    size="lg"
                    className="bg-foreground text-background hover:bg-foreground/90"
                  >
                    Next Question →
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
    <ConceptPopup />
    </>
  )
}
